import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import Toastify styles
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { authToken } from '../globals/Global'; // Assuming this is your global token
import { createArticle, getArticleDetails, updateArticle, deleteArticle, updateArticleContent, deleteArticleContent } from '../globals/Services';

export default function ArticleManagement() {
  // State for categories
  const [categories, setCategories] = useState([]);
  // State for all sub-categories
  const [subCategories, setSubCategories] = useState([]);

  // State for form inputs for creating a new category
  const [newCategory, setNewCategory] = useState({
  titleName: '',
  name: '',
  imageUrl: '',
  imageFile: null,
  articleImages: [],
  description: '',
  articleUrls: ''
});


  // State to manage which category is currently selected in the UI
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // --- Dialog Management State ---
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openEditCategoryDialog, setOpenEditCategoryDialog] = useState(false);
  const [openEditSubCategoryDialog, setOpenEditSubCategoryDialog] = useState(false);
  const [openDeleteCategoryDialog, setOpenDeleteCategoryDialog] = useState(false);
  const [openDeleteSubCategoryDialog, setOpenDeleteSubCategoryDialog] = useState(false);

  // --- State for Items Being Edited or Deleted ---
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deletingSubCategory, setDeletingSubCategory] = useState(null);

  // --- State to trigger data refetching ---
  const [refetch, setRefetch] = useState(0);
  const triggerRefetch = () => setRefetch(prev => prev + 1);

  // --- Data Fetching ---
  const fetchArticleData = useCallback(async () => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      const res = await fetch(getArticleDetails, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await res.json();
      // console.log('API Response:', result); // Debug log
      
      const articleData = result?.data || [];

      const transformedCategories = articleData.map(cat => ({
        id: cat.id,
        titleName: cat.title,
        name: cat.title,
        imageUrl: cat.imageUrl || '',
      }));
      setCategories(transformedCategories);

      // Fixed: articleContentDetails is an object, not an array
      const allSubCategories = articleData
        .filter(cat => cat.articleContentDetails) // Only include articles with content
        .map(cat => {
          const content = cat.articleContentDetails;
          return {
            id: content.id,
            articleCategoryId: cat.id,
            name: cat.title, // Use the main article title as name
            description: content.articleContent || '',
            imageUrl: Array.isArray(content.articleImages) && content.articleImages.length > 0 
              ? content.articleImages[0] 
              : (cat.imageUrl || ''),
            articleUrls: content.articleUrls || '',
            status: content.status || '',
            createdAt: content.createdAt || '',
            updatedAt: content.updatedAt || ''
          };
        });
      
      // console.log('Transformed Sub-categories:', allSubCategories); // Debug log
      setSubCategories(allSubCategories);

      if (!selectedCategoryId && transformedCategories.length > 0) {
        setSelectedCategoryId(transformedCategories[0].id);
      }

    } catch (error) {
      console.error('Failed to fetch Article data:', error);
      toast.error('Error fetching article data');
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    fetchArticleData();
  }, [refetch, fetchArticleData]);

  const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

  // --- Category CRUD Handlers ---
const handleCreateCategory = async () => {
  try {
    const formData = new FormData();
    formData.append("title", newCategory.titleName); // Using titleName as the article title
    formData.append("articleContent", newCategory.description || ""); // Add a description field in newCategory state if needed
    formData.append("articleUrls", newCategory.articleUrls || "");

    // Single main image (file)
    if (newCategory.imageFile) {
      formData.append("file", newCategory.imageFile);
    }

    // Multiple images (articleImages)
    if (newCategory.articleImages && newCategory.articleImages.length > 0) {
      newCategory.articleImages.forEach((imgFile) => {
        formData.append("articleImages", imgFile);
      });
    }

    const res = await fetch(createArticle, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: formData,
    });
    // console.log("formData:", formData); // Debug log
    const result = await res.json();
    if (!res.ok) throw new Error(result.message || "Could not create article.");

    toast.success("Article created successfully!");
    setOpenCategoryDialog(false);
    setNewCategory({ titleName: '', name: '', imageUrl: '', imageFile: null, articleImages: [], description: '', articleUrls: '' });
    triggerRefetch();
  } catch (error) {
    console.error("API error:", error);
    toast.error(`Error: ${error.message}`);
  }
};


  const handleUpdateCategory = async () => {
  if (!editingCategory) return;
  try {
    const formData = new FormData();
    formData.append('articleId', editingCategory.id); // Use .id as per your fetchArticleData mapping
    formData.append('title', editingCategory.titleName || editingCategory.name || '');
    formData.append('articleContent', editingCategory.description || '');

    // If you want to allow updating the image, add a file input in the edit dialog and handle it here:
    if (editingCategory.imageFile) {
      formData.append('file', editingCategory.imageFile);
    }

    const res = await fetch(updateArticle, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        // Do NOT set Content-Type header for FormData; browser will set it automatically
      },
      body: formData,
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.message || 'Could not update category.');

    toast.success('Category updated successfully!');
    setOpenEditCategoryDialog(false);
    triggerRefetch();
  } catch (error) {
    console.error('API error:', error);
    toast.error(`Error: ${error.message}`);
  }
};

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    try {
      const res = await fetch(deleteArticle(deletingCategory.id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete category.');
      }

      toast.success('Category deleted successfully!');
      setOpenDeleteCategoryDialog(false);
      setSelectedCategoryId(null);
      triggerRefetch();
    } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
    }
  };
  
  const handleUpdateSubCategory = async () => {
  if (!editingSubCategory) return;
  try {
    const formData = new FormData();
    formData.append('articleId', editingSubCategory.articleCategoryId);
    formData.append('title', editingSubCategory.name);
    formData.append('articleContent', editingSubCategory.description);
    formData.append('articleUrls', editingSubCategory.articleUrls || '');

    if (editingSubCategory.imageFile) {
      formData.append('file', editingSubCategory.imageFile);
    }
    if (editingSubCategory.additionalImageFile) {
      formData.append('additionalFile', editingSubCategory.additionalImageFile);
    }

    const res = await fetch(updateArticleContent, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: formData,
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.message || 'Could not update article content.');

    toast.success('Article content updated successfully!');
    setOpenEditSubCategoryDialog(false);
    triggerRefetch();
  } catch (error) {
    console.error('API error:', error);
    toast.error(`Error: ${error.message}`);
  }
};

  const handleDeleteSubCategory = async () => {
    if (!deletingSubCategory) return;
    try {
      const res = await fetch(deleteArticleContent(deletingSubCategory.id), {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Could not delete sub-category.');
      }

      toast.success('Sub-category deleted successfully!');
      setOpenDeleteSubCategoryDialog(false);
      triggerRefetch();
    } catch (error) {
        console.error('API error:', error);
        toast.error(`Error: ${error.message}`);
    }
  };

  // --- Dialog Open/Close Handlers ---
  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setOpenEditCategoryDialog(true);
  };
  
  const openDeleteCategory = (cat) => {
      setDeletingCategory(cat);
      setOpenDeleteCategoryDialog(true);
  };

  const openEditSubCategory = (sub) => {
    setEditingSubCategory(sub);
    setOpenEditSubCategoryDialog(true);
  };
  
  const openDeleteSubCategory = (sub) => {
      setDeletingSubCategory(sub);
      setOpenDeleteSubCategoryDialog(true);
  };

  const filteredSubCategories = selectedCategoryId
    ? subCategories.filter(sub => String(sub.articleCategoryId) === String(selectedCategoryId))
    : [];

  const getSubCategoryCount = (categoryId) => {
      return subCategories.filter(sub => String(sub.articleCategoryId) === String(categoryId)).length;
  }
  
  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">
          Article Management
        </Typography>
        <Box display="flex" gap={2}>
        <Button variant="contained" color="primary" onClick={() => setOpenCategoryDialog(true)}
          sx={{ 
          fontWeight: 'bold',
          textTransform: 'none'
        }}
          >
        + Add Article Category
        </Button>

        </Box>

      </Box>

      {/* Main Content Grid */}
      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 100px)' }}>
        
        {/* Left Column - Categories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">Article Categories</Typography>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {categories.length > 0 ? (
                            categories.map(cat => (
                                <Box
                                    key={cat.id}
                                    onClick={() => setSelectedCategoryId(cat.id)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1,
                                        border: 1, borderRadius: 2,
                                        borderColor: selectedCategoryId === cat.id ? 'primary.main' : 'grey.300',
                                        backgroundColor: selectedCategoryId === cat.id ? 'primary.lightest' : 'white',
                                        cursor: 'pointer', '&:hover': { borderColor: 'primary.light', backgroundColor: 'grey.50' }
                                    }}
                                >
                                    <img src={cat.imageUrl || 'https://via.placeholder.com/60'} alt={cat.name} style={{ width: 60, height: 60, borderRadius: '8px', objectFit: 'cover' }}/>
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle1" fontWeight="bold">{cat.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">ID: {cat.id}</Typography>
                                        <Typography variant="body2" color="text.secondary">Title Name: {cat.titleName}</Typography>
                                        <Typography variant="caption" color="primary">{getSubCategoryCount(cat.id)} Article(s)</Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteCategory(cat); }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>No Article categories found.</Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>

        {/* Right Column - Subcategories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Article Content {selectedCategoryId && `(${filteredSubCategories.length} items)`}
                    </Typography>
                     <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {filteredSubCategories.length > 0 ? (
                            filteredSubCategories.map(sub => (
                                <Box key={sub.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1, border: 1, borderRadius: 2, borderColor: 'grey.200' }}>
                                    <img src={sub.imageUrl || 'https://via.placeholder.com/80x60'} alt={sub.name} style={{ width: 80, height: 60, borderRadius: '6px', objectFit: 'cover' }}/>
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="secondary.dark">{sub.name}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          ID: {sub.id} | Status: {sub.status || 'Unknown'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                            {sub.description ? (
                                              sub.description.split(' ').slice(0, 14).join(' ') + 
                                              (sub.description.split(' ').length > 14 ? '...' : '')
                                            ) : 'No content available'}
                                        </Typography>
                                        {sub.articleUrls && (
                                          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                                            URL: {sub.articleUrls}
                                          </Typography>
                                        )}
                                    </Box>
                                     <Box display="flex" flexDirection="column" gap={1}>
                                        <IconButton size="small" color="primary" onClick={() => openEditSubCategory(sub)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => openDeleteSubCategory(sub)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                             <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                {selectedCategoryId ? 'No Article content for this category.' : 'Please select a category.'}
                             </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
      </Box>
      
      {/* --- Dialogs --- */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} fullWidth maxWidth="sm">
      <DialogTitle>Add Article Category</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Article Title"
          margin="normal"
          value={newCategory.titleName}
          onChange={e => setNewCategory({ ...newCategory, titleName: e.target.value })}
        />
        <TextField
          fullWidth
          label="Article Content / Description"
          multiline
          rows={4}
          margin="normal"
          value={newCategory.description}
          onChange={e => setNewCategory({ ...newCategory, description: e.target.value })}
        />
        <TextField
          fullWidth
          label="Article URL"
          margin="normal"
          value={newCategory.articleUrls}
          onChange={e => setNewCategory({ ...newCategory, articleUrls: e.target.value })}
        />
        <Box mt={2}>
          <Typography variant="body2" fontWeight="bold">Upload Main Thumbnail Image</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewCategory({ ...newCategory, imageFile: e.target.files[0] })}
          />
        </Box>
        <Box mt={2}>
          <Typography variant="body2" fontWeight="bold">Upload Additional Images</Typography>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setNewCategory({ ...newCategory, articleImages: Array.from(e.target.files) })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
        <Button onClick={handleCreateCategory} variant="contained">Create</Button>
      </DialogActions>
    </Dialog>


      <Dialog open={openEditCategoryDialog} onClose={() => setOpenEditCategoryDialog(false)} fullWidth maxWidth="sm">
  <DialogTitle>Edit Article Category</DialogTitle>
  {editingCategory && (
    <>
      <DialogContent>
        <TextField
          fullWidth
          label="Title Name"
          margin="normal"
          value={editingCategory.titleName}
          onChange={e => setEditingCategory({ ...editingCategory, titleName: e.target.value })}
        />
        <Box mt={2}>
          <Typography variant="body2" fontWeight="bold">Upload New Thumbnail Image</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={e => setEditingCategory({ ...editingCategory, imageFile: e.target.files[0] })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenEditCategoryDialog(false)}>Cancel</Button>
        <Button onClick={handleUpdateCategory} variant="contained">Save Changes</Button>
      </DialogActions>
    </>
  )}
</Dialog>
      
      <Dialog open={openDeleteCategoryDialog} onClose={() => setOpenDeleteCategoryDialog(false)} fullWidth maxWidth="xs">
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
              <Typography>Are you sure you want to delete the category: <strong>{deletingCategory?.name}</strong>?</Typography>
              <Typography color="error" variant="body2">This will also delete all associated Article content and cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setOpenDeleteCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleDeleteCategory} variant="contained" color="error">Delete</Button>
          </DialogActions>
      </Dialog>
      
      <Dialog open={openEditSubCategoryDialog} onClose={() => setOpenEditSubCategoryDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Edit Article Content</DialogTitle>
          {editingSubCategory && (
              <>
                  <DialogContent>
                      <FormControl fullWidth margin="normal">
                          <InputLabel id="edit-article-category-select-label">Category</InputLabel>
                          <Select
                              labelId="edit-article-category-select-label"
                              value={editingSubCategory.articleCategoryId}
                              label="Category"
                              onChange={e => setEditingSubCategory({ ...editingSubCategory, articleCategoryId: e.target.value })}
                          >
                              {categories.map(cat => (
                                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                              ))}
                          </Select>
                      </FormControl>
                      <TextField fullWidth label="Article Content / Description" multiline rows={4} margin="normal" value={editingSubCategory.description} onChange={e => setEditingSubCategory({ ...editingSubCategory, description: e.target.value })} />
                      <TextField fullWidth label="Article URLs" margin="normal" value={editingSubCategory.articleUrls || ''} onChange={e => setEditingSubCategory({ ...editingSubCategory, articleUrls: e.target.value })} />
                        <Box mt={2}>
                        <Typography variant="body2" fontWeight="bold">Upload Additional Image</Typography>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={e => setEditingSubCategory({ ...editingSubCategory, additionalImageFile: e.target.files[0] })}
                        />
                        </Box>
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={() => setOpenEditSubCategoryDialog(false)}>Cancel</Button>
                      <Button onClick={handleUpdateSubCategory} variant="contained">Save Changes</Button>
                  </DialogActions>
              </>
          )}
      </Dialog>
      
      <Dialog open={openDeleteSubCategoryDialog} onClose={() => setOpenDeleteSubCategoryDialog(false)} fullWidth maxWidth="xs">
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
              <Typography>Are you sure you want to delete the Article: <strong>{deletingSubCategory?.name}</strong>?</Typography>
               <Typography color="error" variant="body2">This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setOpenDeleteSubCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleDeleteSubCategory} variant="contained" color="error">Delete</Button>
          </DialogActions>
      </Dialog>
      
      {/* Add ToastContainer at the end */}
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            
    </Box>
  );
}